import React, { useEffect, useState } from "react";
import * as FeatherIcon from "react-feather";
import iq_api from "../../services/iqApi";
import { useUser } from "../../context/UserContext";
import Alert from "../HtmlElements/Alert";

const MarketForecastTool = () => {
  const { user } = useUser();
  const userId = user?.user_id;

  const [showModal, setShowModal] = useState(false);
  const [marketName, setMarketName] = useState("");

  const [isGenerating, setIsGenerating] = useState(false);
  const [loadingList, setLoadingList] = useState(false);

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [mainError, setMainError] = useState("");
  const [mainSuccessMessage, setMainSuccessMessage] = useState("");

  const [list, setList] = useState([]);

  // ---------------- Fetch Listing ----------------
  const fetchList = async () => {
    try {
      setLoadingList(true);

      const res = await iq_api.get("/market_forecast/list", {
        params: { user_id: userId },
      });

      setList(Array.isArray(res.data?.jobs) ? res.data.jobs : []);
      setLoadingList(false);
    } catch (err) {
      console.error(err);
      setList([]);
      setLoadingList(false);
    }
  };

  useEffect(() => {
    if (!userId) return;

    fetchList();

    const interval = setInterval(() => {
      fetchList();
    }, 30000);

    return () => clearInterval(interval);
  }, [userId]);

  // ---------------- Create Forecast ----------------
  const handleCreate = async () => {
    if (!marketName.trim()) {
      setError("Market Name is required.");
      setTimeout(() => setError(""), 4000);
      return;
    }

    try {
      setShowModal(false);
      setIsGenerating(true);

      const payload = {
        market_name: marketName,
        output_dir: "/tmp/market_forecasts",
        user_id: userId,
      };

      const res = await iq_api.post("/market_forecast", payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!res.data?.task_id) {
        throw new Error("task_id missing");
      }

      setMainSuccessMessage("Market Forecast request submitted successfully.");
      setTimeout(() => setMainSuccessMessage(""), 5000);

      setIsGenerating(false);
      fetchList();
    } catch (err) {
      console.error(err);
      setMainError("Failed to create Market Forecast.");
      setTimeout(() => setMainError(""), 5000);
      setIsGenerating(false);
    }
  };

  // ---------------- Download ----------------
  const handleDownload = async (taskId) => {
    try {
      const res = await iq_api.get(`/market_forecast/${taskId}/download`, {
        responseType: "blob",
      });

      const blob = new Blob([res.data]);
      const link = document.createElement("a");

      const type = res.headers["content-type"] || "";
      const ext = type.includes("pdf")
        ? "pdf"
        : type.includes("word") || type.includes("docx")
        ? "docx"
        : "file";

      link.href = URL.createObjectURL(blob);
      link.download = `Market_Forecast_${taskId}.${ext}`;
      link.click();
    } catch (err) {
      console.error(err);
      alert("Failed to download document.");
    }
  };

  // ---------------- Open Modal ----------------
  const openModal = () => {
    setMarketName("");
    setError("");
    setSuccessMessage("");
    setShowModal(true);
  };

  return (
    <>
      <div className="container-fluid">
        <div className="row">
          <div
            className="all_acccounts_wrapper"
            style={{
              backgroundColor: "#fff",
              padding: 20,
              borderRadius: 12,
              maxWidth: "99%",
              minHeight: "100vh",
            }}
          >
            <div className="p-4">
              {mainSuccessMessage && (
                <Alert type="success" message={mainSuccessMessage} />
              )}
              {mainError && <Alert type="danger" message={mainError} />}

              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4>Market Forecast Generator</h4>

                <div>
                  <button
                    className="btn btn-outline-secondary m-2"
                    style={{
                      padding: "5px 10px",
                      backgroundColor: "rgb(35, 36, 40)",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#fff",
                      border: "none",
                      borderRadius: "6px",
                    }}
                    onClick={fetchList}
                    disabled={loadingList}
                  >
                    <FeatherIcon.RefreshCw size={14} /> Refresh
                  </button>

                  <button
                    className="btn btn-primary"
                    style={{
                      padding: "5px 10px",
                      backgroundColor: "rgb(35, 36, 40)",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#fff",
                      border: "none",
                      borderRadius: "6px",
                    }}
                    onClick={openModal}
                    disabled={isGenerating}
                  >
                    <FeatherIcon.FilePlus size={14} /> Create Forecast
                  </button>
                </div>
              </div>

              {isGenerating && (
                <div className="text-center p-5">
                  <div className="spinner-border text-primary mb-3" />
                  <p className="fw-semibold">
                    Submitting your request… Please wait.
                  </p>
                </div>
              )}

              {/* Listing */}
              <div className="mt-4">
                <h5>Generated Documents</h5>

                <div className="table-responsive">
                  <table className="table custom-resizable-table">
                    <thead className="table-light">
                      <tr className="custom-resizable-table-header">
                        <th>#</th>
                        <th>Market Name</th>
                        <th>Status</th>
                        <th>Created At</th>
                        <th>Completed At</th>
                        <th>Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {list.length === 0 && (
                        <tr>
                          <td colSpan="6" className="text-center">
                            No records found.
                          </td>
                        </tr>
                      )}

                      {list.map((item, index) => {
                        const createdAt = item?.created_at
                          ? new Date(item.created_at)
                          : null;

                        const completedAt = item?.completed_at
                          ? new Date(item.completed_at)
                          : null;

                        return (
                          <tr key={item.task_id}>
                            <td>{index + 1}</td>
                            <td>{item.market_name}</td>

                            <td>
                              {item.status === "completed" ? (
                                <span className="badge bg-success">
                                  Completed
                                </span>
                              ) : (
                                <span className="badge bg-warning text-dark">
                                  {item.status}
                                </span>
                              )}
                            </td>

                            <td>
                              {createdAt ? createdAt.toLocaleString() : "-"}
                            </td>

                            <td>
                              {completedAt ? completedAt.toLocaleString() : "-"}
                            </td>

                            <td>
                              {item.status === "completed" ? (
                                <button
                                  className="btn btn-sm btn-primary"
                                  style={{
                                    padding: "5px 10px",
                                    backgroundColor: "rgb(35,36,40)",
                                    fontSize: "13px",
                                    fontWeight: "600",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "6px",
                                  }}
                                  onClick={() => handleDownload(item.task_id)}
                                >
                                  <FeatherIcon.Download size={14} /> Download
                                </button>
                              ) : (
                                <span className="text-muted">
                                  Processing...
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      <div className={`modal fade ${showModal ? "show d-block" : ""}`}>
        <div className="modal-dialog modal-md">
          <div className="modal-content">
            <div className="modal-header">
              <h5>Create Market Forecast</h5>

              <button
                className="btn-close"
                onClick={() => setShowModal(false)}
              />
            </div>

            <div className="modal-body">
              {successMessage && (
                <Alert type="success" message={successMessage} />
              )}

              {error && <Alert type="danger" message={error} />}

              <label className="form-label fw-semibold">
                Market Name <span className="red">*</span>
              </label>

              <input
                className="form-control"
                value={marketName}
                onChange={(e) => setMarketName(e.target.value)}
              />
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowModal(false)}
              >
                Close
              </button>

              <button
                className="btn btn-primary"
                style={{ background: "#4E6BCA" }}
                onClick={handleCreate}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      </div>

      {showModal && <div className="modal-backdrop fade show"></div>}
    </>
  );
};

export default MarketForecastTool;
